import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highmaps";
import { useWindowSize } from "~/utils/useWindowSize";
import topology from "@highcharts/map-collection/custom/world.topo.json";
import ReactDOMServer from "react-dom/server";
import { MapTooltip } from "./MapTooltip";
import { RWA_COLORS } from "~/styles/colors";
import { DEFAULT_TOOLTIP_PROPS } from "~/modules/visualization/consts";
import { Asset } from "~/modules/assets/types";
import { VisualizationCard } from "~/modules/visualization/components/VisualizationCard";
import { CountryData } from "./types";
import { WindowResizeObserver } from "~/components/WindowResizeObserver";

// // // //

function getChartOptions(props: { assets: Asset[]; smScreen: boolean }) {
    const assetsWithCountries = props.assets.filter(
        (a) => a.real_estate_property !== null,
    );

    const countriesData = assetsWithCountries.reduce<
        Record<string, CountryData>
    >((acc, a) => {
        // Get the country data for the asset
        const country = a.real_estate_property?.country;

        // If the country is not found or the alpha2 code is not found, return the accumulator
        if (!country || !country.alpha2_code) {
            return acc;
        }

        // Get the country data for the country
        const countryData: CountryData = acc[country.alpha2_code] || {
            name: country.name,
            alpha2_code: country.alpha2_code,
            slug: country.slug,
            icon_url: country.icon_url,
            asset_count: 0,
            value: 0,
        };

        // Accumulate the value of the assets in the country
        countryData.value =
            countryData.value + (a.total_asset_value_dollar?.val || 0);

        // Accumulate the asset count of the assets in the country
        countryData.asset_count = countryData.asset_count + 1;

        // Return the updated accumulator
        return {
            ...acc,
            [country.alpha2_code]: countryData,
        };
    }, {});

    // Map the countries data to the chart data
    const chartData = Object.values(countriesData).map((c) => {
        return {
            ...c,
            color: RWA_COLORS["blue"]["400"],
            code: c.alpha2_code,
            value: c.value,
        };
    });

    const opts = {
        chart: {
            map: topology,
            animation: false,
            spacing: [0, 0, 0, 0],
            backgroundColor: "transparent",
            panning: { enabled: false },
        },
        credits: {
            enabled: false,
        },
        title: null,
        subtitle: null,
        legend: {
            enabled: false,
        },
        mapNavigation: {
            enabled: false,
            buttonOptions: {
                verticalAlign: "bottom",
            },
        },
        mapView: {
            projection: {
                name: "EqualEarth",
            },
            zoom: 1.5,
        },
        tooltip: {
            ...DEFAULT_TOOLTIP_PROPS,
            useHTML: true,
            formatter: function (this: any) {
                const point = this.point;
                const country: CountryData = point.options;

                // Render the MapTooltip component
                return ReactDOMServer.renderToString(
                    <MapTooltip country={country} />,
                );
            },
        },
        plotOptions: {
            series: {
                animation: false, // Disable animation
                states: {
                    inactive: {
                        opacity: 0.6,
                    },
                },
            },
        },

        series: [
            {
                type: "map",
                joinBy: ["iso-a2", "code"],
                data: chartData,
                borderColor: "var(--border)",
                nullColor: "var(--background-02)",
                borderWidth: 1.5,
            },
        ],
    };
    return opts;
}

// // // //

export function RealEstateMapVisualization(props: { assets: Asset[] }) {
    const { assets } = props;

    const windowSize = useWindowSize();

    // HighchartsMap(Highcharts);

    const options = getChartOptions({
        assets,
        smScreen: windowSize.isSm,
    });

    return (
        <VisualizationCard
            title="Tokenized Real Estate by Country"
            colSpans={{
                sm: 12,
                md: 12,
                lg: 12,
                xl: 6,
                xxl: 6,
            }}
        >
            <WindowResizeObserver defaultPlaceholderHeight={390}>
                <HighchartsReact
                    highcharts={Highcharts}
                    constructorType="mapChart"
                    options={options}
                />
            </WindowResizeObserver>
        </VisualizationCard>
    );
}
